import React, { useMemo } from 'react';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
interface CSVPreviewProps {
  csvPreviewData: any[];
  showPreview: boolean;
  onTogglePreview: () => void;
  columnDisplayNames?: Record<string, string>;
  columnOrder?: string[];
  excludeColumns?: string[];
}
const DEFAULT_EXCLUDE_COLUMNS = ['rowNumber'];
const ROW_NUMBER_WIDTH = 70;
const FIRST_DATA_COL_WIDTH = 160;
const formatColumnName = (key: string): string => {
  const cleanedKey = String(key ?? '').trim();
  if (!cleanedKey) return 'Column';
  const spacedKey = cleanedKey
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!spacedKey) return 'Column';
  return spacedKey
    .split(' ')
    .map((word) => {
      if (!word) return '';
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};
const getColumnDisplayName = (
  key: string,
  customMapping?: Record<string, string>,
): string => {
  if (customMapping && customMapping[key]) {
    return customMapping[key];
  }
  return formatColumnName(key);
};
const getOrderedColumns = (
  sensors: any[],
  customOrder?: string[],
  excludeColumns?: string[],
): string[] => {
  if (sensors.length === 0) return [];
  const excludeSet = new Set<string>(
    [...DEFAULT_EXCLUDE_COLUMNS, ...(excludeColumns || [])].filter(Boolean),
  );
  const orderedKeys: string[] = [];
  const seen = new Set<string>();
  sensors.forEach((sensor) => {
    Object.keys(sensor).forEach((key) => {
      if (excludeSet.has(key) || seen.has(key)) {
        return;
      }
      seen.add(key);
      orderedKeys.push(key);
    });
  });
  if (!customOrder || customOrder.length === 0) {
    return orderedKeys;
  }
  const preferredOrder = customOrder.filter(
    (key) => !excludeSet.has(key) && orderedKeys.indexOf(key) !== -1,
  );
  const remainingColumns = orderedKeys.filter(
    (key) => preferredOrder.indexOf(key) === -1,
  );
  return [...preferredOrder, ...remainingColumns];
};
export const CSVPreview: React.FC<CSVPreviewProps> = ({
  csvPreviewData,
  showPreview,
  onTogglePreview,
  columnDisplayNames,
  columnOrder,
  excludeColumns,
}) => {
  const columns = useMemo(() => {
    return getOrderedColumns(csvPreviewData, columnOrder, excludeColumns);
  }, [csvPreviewData, columnOrder, excludeColumns]);
  /** * DataGrid columns. * * We intentionally do not use MUI's `pinnedColumns` * prop because the installed DataGrid version does not * expose that API. */
  const gridColumns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'rowNumber',
        headerName: '#',
        width: ROW_NUMBER_WIDTH,
        sortable: false,
        filterable: false,

        cellClassName: 'csv-sticky-row-number',
        headerClassName: 'csv-sticky-row-number',
      },

      ...columns.map((column, index) => {
        const isFirstDataColumn = index === 0;

        return {
          field: column,

          headerName: getColumnDisplayName(column, columnDisplayNames),

          minWidth: isFirstDataColumn ? FIRST_DATA_COL_WIDTH : 140,

          width: isFirstDataColumn ? FIRST_DATA_COL_WIDTH : undefined,

          flex: isFirstDataColumn ? undefined : 1,

          ...(isFirstDataColumn
            ? {
                cellClassName: 'csv-sticky-first-data',

                headerClassName: 'csv-sticky-first-data',
              }
            : {}),
        };
      }),
    ],
    [columns, columnDisplayNames],
  );
  const gridRows = useMemo(
    () =>
      csvPreviewData.map((row, index) => ({
        ...row,
        id: `${row.rowNumber || index + 1}-${index}`,
        rowNumber: row.rowNumber || index + 1,
        ...columns.reduce<Record<string, string>>((values, column) => {
          const value = row[column];
          values[column] =
            value !== undefined && value !== null && value !== ''
              ? String(value)
              : '-';
          return values;
        }, {}),
      })),
    [csvPreviewData, columns],
  );
  if (csvPreviewData.length === 0) {
    return null;
  }
  return (
    <div className="border border-gray-200 rounded-lg">
      {' '}
      <div className="flex items-center justify-between p-4 bg-gray-50 border-b border-gray-200">
        {' '}
        <h4 className="text-sm font-medium text-gray-700">
          {' '}
          Preview Data ({csvPreviewData.length} rows, {columns.length}{' '}
          columns){' '}
        </h4>{' '}
        <button
          onClick={onTogglePreview}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          {' '}
          {showPreview ? 'Hide Preview' : 'Show Preview'}{' '}
        </button>{' '}
      </div>{' '}
      {showPreview && (
        <div className="border-t border-gray-200">
          {' '}
          <DataGrid
            rows={gridRows}
            columns={gridColumns}
            density="compact"
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  page: 0,
                  pageSize: 10,
                },
              },
            }}
            autoHeight
            // IMPORTANT:
            // Keep all columns in the DOM so CSS position: sticky
            // can work on the first column.
            disableVirtualization
            sx={{
              border: 0,

              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: '#f9fafb',
              },

              /*
               * IMPORTANT:
               * DataGrid cells are positioned by the grid.
               * !important is required so our sticky positioning wins.
               */

              // ============================
              // # COLUMN
              // ============================

              '& .csv-sticky-row-number': {
                position: 'sticky !important',
                left: '0px !important',
                zIndex: '10 !important',
                backgroundColor: '#fff !important',
              },

              '& .MuiDataGrid-columnHeader.csv-sticky-row-number': {
                position: 'sticky !important',
                left: '0px !important',
                zIndex: '20 !important',
                backgroundColor: '#f9fafb !important',
              },

              // ============================
              // FIRST CSV COLUMN
              // ============================

              '& .csv-sticky-first-data': {
                position: 'sticky !important',
                left: `${ROW_NUMBER_WIDTH}px !important`,
                zIndex: '9 !important',
                backgroundColor: '#fff !important',
                borderRight: '1px solid #e5e7eb !important',
              },

              '& .MuiDataGrid-columnHeader.csv-sticky-first-data': {
                position: 'sticky !important',
                left: `${ROW_NUMBER_WIDTH}px !important`,
                zIndex: '19 !important',
                backgroundColor: '#f9fafb !important',
                borderRight: '1px solid #e5e7eb !important',
              },

              // ============================
              // NORMAL CELLS
              // ============================

              '& .MuiDataGrid-cell': {
                backgroundColor: '#fff',
              },

              // Sticky cells must paint over scrolling cells
              '& .MuiDataGrid-cell.csv-sticky-row-number': {
                backgroundColor: '#fff !important',
              },

              '& .MuiDataGrid-cell.csv-sticky-first-data': {
                backgroundColor: '#fff !important',
              },

              // ============================
              // HEADER
              // ============================

              '& .MuiDataGrid-columnHeader': {
                backgroundColor: '#f9fafb',
              },
            }}
          />
        </div>
      )}{' '}
    </div>
  );
};
